import { queryOptions, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCurrentUser, useWorkspace } from 'components/providers'
import { pick } from 'lodash-es'
import { useCallback, useEffect, useState } from 'react'
import {
  NavigateOptions,
  createSearchParams,
  generatePath,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router'
import {
  DBProps,
  FirestoreFilter,
  IUser,
  IWorkspace,
  OmitDBProps,
  Permission,
  User,
  WorkspaceScope,
  WorkspaceUser,
  dbProps,
} from 'types'
import { getDocs } from './callables'
import { getDocById } from './db'
import { useEntevatePermission } from './preferences'

export type WorkspaceRecord = Pick<
  IWorkspace,
  keyof DBProps | (typeof partialWorkspaceFields)[number]
>

const partialWorkspaceFields = [
  'isArchived',
  'scope',
  'title',
] as const satisfies (keyof IWorkspace)[]

export const WorkspacePath = '/:workspaceId'

/** Go to a workspace */
export function useGoToWorkspace() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  function goToWorkspace(
    workspaceId?: string,
    search?: { [prop: string]: string } | null,
    options?: NavigateOptions
  ) {
    if (!workspaceId) return

    let _search = searchParams.toString()
    if (search) _search = createSearchParams(search).toString()

    navigate({ pathname: generatePath(WorkspacePath, { workspaceId }), search: _search }, options)
  }

  return goToWorkspace
}

export function useSwitchWorkspace() {
  const navigate = useNavigate()

  function switchWorkspace(workspaceId?: string, routePath?: string, options?: NavigateOptions) {
    if (!workspaceId) return

    navigate({ pathname: generatePath(`${WorkspacePath}/${routePath}`, { workspaceId }) }, options)
  }

  return switchWorkspace
}

/** Go back or to root of workspace if default route */
export default function useGoBack() {
  const { workspaceId } = useParams()
  const goToWorkspace = useGoToWorkspace()
  const location = useLocation()
  const navigate = useNavigate()

  function goBack() {
    const firstRoute = location.key === 'default'
    if (firstRoute) goToWorkspace(workspaceId)
    else navigate(-1)
  }

  return goBack
}

const makeQueryKey = (scope?: WorkspaceScope, userId?: string) => ['workspaces', scope, userId]

export const makeWorkspaceKey = (workspaceId?: string) => ['workspace', workspaceId]

export function useWorkspaces(scope: WorkspaceScope, removeInternal?: boolean) {
  const user = useCurrentUser()
  const userId = user?.isAdmin ? undefined : user?.id

  const { data, ...rest } = useQuery({
    queryKey: makeQueryKey(scope, userId),
    staleTime: 1000 * 30,
    queryFn: () => getWorkspaces(scope, userId),
  })

  const workspaces = removeInternal
    ? data?.filter(w => !internalWorkspaceTitles.includes(w.title))
    : data
  return { workspaces, ...rest }
}

export const internalWorkspaceTitles = ['ENTEVATE', 'Playground', 'Apple Inc.', 'Acme Inc.']

export function useMutateWorkspaceRecords() {
  const user = useCurrentUser()
  const userId = user?.isAdmin ? undefined : user?.id
  const queryClient = useQueryClient()

  function updateWorkspaceRecord(body: IWorkspace) {
    const record = makeWorkspaceRecord(body)
    if (record.isArchived) return removeWorkspaceRecord(record.id)

    queryClient.setQueriesData<WorkspaceRecord[]>(
      { queryKey: makeQueryKey('emp', userId) },
      workspaces => {
        if (!workspaces) return [record]

        const workspaceIndex = workspaces.findIndex(w => w.id === record.id)
        if (workspaceIndex < 0) return sortWorkspaceRecords([...workspaces, record])

        return sortWorkspaceRecords(workspaces.map((w, i) => (i === workspaceIndex ? record : w)))
      }
    )
  }

  function removeWorkspaceRecord(id: string) {
    queryClient.setQueriesData<WorkspaceRecord[]>(
      { queryKey: makeQueryKey('emp', userId) },
      workspaces => {
        if (!workspaces) return []

        const workspaceIndex = workspaces.findIndex(w => w.id === id)
        if (workspaceIndex < 0) return workspaces

        return workspaces.filter((_, i) => i !== workspaceIndex)
      }
    )
  }

  return { updateWorkspaceRecord, removeWorkspaceRecord }
}

async function getWorkspaces(scope: WorkspaceScope, userId?: string) {
  let constraints: FirestoreFilter<keyof IWorkspace>[] = [
    { field: 'scope', op: '==', value: scope },
    { field: 'isArchived', op: '==', value: false },
  ]

  if (userId) {
    constraints.splice(2, 0, {
      field: 'userIds',
      op: 'array-contains',
      value: userId,
    })
  }

  const workspaces = await getDocs({
    collection: 'Workspace',
    constraints,
    sort: { by: 'title', order: 'ASC' },
    fields: partialWorkspaceFields,
  })

  return workspaces.data.map(makeWorkspaceRecord)
}

function sortWorkspaceRecords(records: WorkspaceRecord[]) {
  return records.sort((a, b) => (a.title || '').localeCompare(b.title || ''))
}

function makeWorkspaceRecord(workspace: WorkspaceRecord): WorkspaceRecord {
  const partial = pick(workspace, [...dbProps, ...partialWorkspaceFields])
  return { ...partial } as WorkspaceRecord
}

export const useWorkspaceRecord = (workspaceId?: string) => {
  const { workspaces } = useWorkspaces('emp')
  return workspaces?.find(w => w.id === workspaceId)
}

export const makeEmptyWorkspace = (scope: IWorkspace['scope']): OmitDBProps<IWorkspace> => ({
  pendingUserByEmail: {},
  scope,
  title: '',
  isArchived: false,
  userById: {},
  userIds: [],
  traitTagById: {},
})

export function usePermission(): Permission {
  const { workspace } = useWorkspace()
  const user = useCurrentUser()
  const [entevatePermission] = useEntevatePermission()

  if (!workspace || !user) return {}

  if (user.isAdmin) {
    const permission = entevatePermission === 'entevate' ? 'admin' : entevatePermission
    return { content: permission, event: permission, facility: permission, workspace: permission }
  }

  const workspacePermission = workspace.userById[user.id]?.permission

  return {
    content: workspacePermission,
    event: workspacePermission,
    facility: workspacePermission,
    workspace: workspacePermission,
  }
}

export function useInitEntevatePermission() {
  const [entevatePermission, setEntevatePermission] = useEntevatePermission()
  const user = useCurrentUser()

  useEffect(() => {
    if (!user) return

    if (user.isAdmin) {
      if (!entevatePermission) setEntevatePermission('entevate')
    } else {
      if (entevatePermission) setEntevatePermission(undefined)
    }
  }, [user])
}

export function useUser(id?: string) {
  const options = useUserQueryOptions()
  return useQuery(options(id))?.data
}

export function useUsers(userIds: string[]) {
  const options = useUserQueryOptions()
  return useQueries({
    queries: userIds.map(options),
    combine: res => ({
      data: res.map(r => r.data).filter(Boolean),
      loading: res.some(r => r.isLoading),
    }),
  })
}

function useUserQueryOptions() {
  const { workspace } = useWorkspace()
  const select = useCallback((dbUser: IUser | null) => makeUser(dbUser, workspace), [workspace])

  return (id?: string) =>
    queryOptions({
      queryKey: ['users', workspace, id],
      queryFn: () => getDocById('users', id!),
      enabled: !!id,
      staleTime: 1000 * 60,
      select,
    })
}

export function makeUser(userDoc: IUser | WorkspaceUser | null, workspace?: IWorkspace | null) {
  if (!userDoc || !workspace) return

  const displayName = makeUserDisplayName(userDoc)

  const pendingUser = workspace.pendingUserByEmail[userDoc.email]
  if (pendingUser) return { ...userDoc, ...pendingUser, displayName, status: 'pending' } as User

  if (!('id' in userDoc)) throw new Error('workspace users must have a User document')

  const workspaceUser = workspace.userById[userDoc.id]
  const status: 'active' | 'pending' = workspaceUser ? 'active' : 'pending'
  return { ...userDoc, ...workspaceUser, displayName, status } as User
}

export const makeUserDisplayName = (user?: Partial<IUser> | null) =>
  user?.name || user?.email || user?.id

export function useWorkspacePrompt() {
  const { workspace } = useWorkspace()
  const [prompt, setPrompt] = useState<string>()

  useEffect(() => {
    if (!workspace) return
    const fetchPrompt = async () => {
      const workspaceUseCase = workspace.useCase || 'default'
      const workspacePrompt = await getDocById('prompts', workspaceUseCase)
      setPrompt(workspacePrompt?.prompt)
    }
    fetchPrompt()
  }, [workspace])

  return prompt
}
